import { useEffect } from 'react'
import { GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import {
  getGladlySupportAnswersBySection,
  searchGladlySupportAnswers,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { GladlyAnswerIndex, GladlyConfig, GladlySection } from 'libs/utils'
import { GladlyProductHelpCenter } from '../components/GladlyProductHelpCenter'
import { helpCenterLayout, useGladlySearchContext } from '../components/ui'
import { getProductHelpCenterRedirect } from '../getServerSideProps'
import { refreshHelpCenterAnonUuidCookie } from '../helpCenterUuidCookie'
import { BrandKey, productsByKey } from '../utils'

interface ProductHelpCenterPageProps {
  brandKey: BrandKey
  config: GladlyConfig
  sections?: GladlySection[] | null
  results?: GladlyAnswerIndex[] | null
  search: string | null
}

const ProductHelpCenterPage = ({
  brandKey,
  config,
  sections,
  results = null,
  search,
}: ProductHelpCenterPageProps) => {
  const { setConfig, setResults } = useGladlySearchContext()

  useEffect(() => {
    setConfig(config)
    setResults(results)
  }, [config, results, search, setConfig, setResults])

  return (
    <>
      <Head>
        <title>{`${search ? `search: ${search} |` : ''} ${productsByKey[brandKey].name} Help Center | MasterClass`}</title>
      </Head>
      <GladlyProductHelpCenter
        brandKey={brandKey}
        sections={sections}
        pageResults={results}
        search={search}
      />
    </>
  )
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const result = await getProductHelpCenterRedirect(ctx)

  if (result.redirect) {
    return result
  }

  refreshHelpCenterAnonUuidCookie(ctx)

  const { brandKey, search } = result

  return getPageData({
    criticalPromises: {
      data: search
        ? searchGladlySupportAnswers(brandKey, search)
        : getGladlySupportAnswersBySection(brandKey),
    },
    propsMapper: (data) => ({
      ...data.data,
      brandKey,
      search,
    }),
  })
}

ProductHelpCenterPage.pageSettings = {
  analyticsLabel: 'Help Center | Product Hub',
  rumLabel: 'Help Center | Product Hub',
  navigation: CoreNavigation,
  footer: RegularFooter,
}

ProductHelpCenterPage.getLayout = helpCenterLayout({ withSearch: true })

export default ProductHelpCenterPage
